/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select from '@tds/core-select';
import TextArea from '@tds/core-text-area';
import Button from '@tds/core-button';
import { Box } from '../design/css';
import request from '../utils/request';
import { orderStatus as statusConstants } from '../utils/constant';
import ValidateLoader from '../UI/validateLoader';
import MessageNotification from '../messageNotification';
import TypeAhead from './typeAhead';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';

const UpdateOrder = ({
  orderStatus,
  sovStatus,
  setOpen,
  open,
  order,
  onSave,
  users,
  assignedUserId,
  userRights,
  labels,
  children
}) => {
  const { incomplete, validated, waitingOnCredit } = statusConstants;
  const getOrderStatus = () => {
    if (sovStatus) return sovStatus;
    if (orderStatus === incomplete || orderStatus === validated) {
      return orderStatus;
    }
    return '';
  };

  const [status, setStatus] = useState(orderStatus || '');
  const [comment, setComment] = useState();
  const [loading, setloading] = useState();
  const [error, setError] = useState();
  const [defaultAssignee, setDefaultAssignee] = useState([]);
  const [assignee, setAssignee] = useState(defaultAssignee);

  const isDisabled = orderStatus === 'ORDER_VALIDATED';
  const isStatusChanged = status !== orderStatus;
  const isAssigneeUpdated = defaultAssignee !== assignee;
  const isOrderUpdated = isStatusChanged || comment || isAssigneeUpdated;

  const localizedString = useLocalizedStringsContext();
  const {
    ORDERS: { statusOptions }
  } = localizedString;

  const {
    pendingValidation,
    assign,
    unassigned,
    update,
    noChange,
    add,
    save,
    cancel,
    loadingContentMessage
  } = labels;
  useEffect(() => {
    if (users && assignedUserId && !assignee.length) {
      const currentAssignee = users.filter(user => user.id === assignedUserId);
      setDefaultAssignee(currentAssignee);
      setAssignee(currentAssignee);
    }
  }, [assignedUserId, users]);

  const getAssigneeId = () => {
    if (!isAssigneeUpdated) return undefined;
    return assignee.length ? assignee[0].id : -1;
  };

  const addComment = () => {
    const commentsBody = {
      text: comment
    };
    return request.post(`/orders/${order.id}/comments`, commentsBody);
  };

  const updateOrder = () => {
    setloading(true);
    const updateBody = {
      status: status === waitingOnCredit ? undefined : status,
      sovStatus: status === waitingOnCredit ? status : '',
      assignedUserId: getAssigneeId(),
      step: status === statusConstants.incomplete ? 1 : undefined
    };

    return request.put(`/orders/${order.id}`, updateBody);
  };

  const onStatusChange = ({ target }) => {
    setStatus(target.value);
  };

  const closeModal = () => {
    if (isOrderUpdated && !error) {
      onSave({ isUpdated: true, orderId: order.orderId });
      setOpen(!open);
    }
  };

  const onClickSave = async () => {
    if (!isOrderUpdated) return;
    try {
      setloading(true);
      if (comment) {
        await addComment();
      }
      if (isStatusChanged || isAssigneeUpdated) {
        await updateOrder();
      }
      closeModal();
    } catch (err) {
      setError(err.message);
    }
    setloading(false);
  };

  const onClickCancel = () => {
    setOpen(!open);
  };

  const buttons = [
    {
      label: save,
      onClick: onClickSave,
      variant: 'secondary'
    },
    {
      label: cancel,
      onClick: onClickCancel,
      variant: 'primary'
    }
  ];
  return (
    <>
      {loading && <ValidateLoader message={loadingContentMessage} />}
      {userRights && userRights.canAssignOrder && (
        <Box display="flex" width={30} pt={24} pb={24}>
          <TypeAhead
            label={assign}
            onChange={value => setAssignee(value)}
            options={users}
            selected={assignee}
            disabled={isDisabled}
            placeholder={unassigned}
          />
        </Box>
      )}
      <Box>{children}</Box>
      {error && <MessageNotification variant="error" message={error} />}
      <Box display="flex" width={100} mdWidth={100} pt={24} pb={24}>
        {userRights && userRights.isStatusEditable && (
          <Box width={100}>
            <Select
              label={update}
              placeholder={pendingValidation}
              options={statusOptions}
              onChange={onStatusChange}
              defaultValue={getOrderStatus()}
              disabled={isDisabled}
              hint={isDisabled && noChange}
            />
          </Box>
        )}
        <Box width={100}>
          <TextArea
            label={add}
            id="order-validation"
            disabled={isDisabled}
            onChange={event => setComment(event.target.value)}
          />
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="space-evenly"
        flexDirection="row"
        width={100}
        mdWidth={100}
        pr={16}
        pl={16}
        pt={28}
        pb={24}
      >
        {buttons.map(({ label, onClick, variant }) => {
          return (
            <Button variant={variant} onClick={onClick}>
              {label}
            </Button>
          );
        })}
      </Box>
    </>
  );
};

UpdateOrder.propTypes = {
  orderStatus: PropTypes.string.isRequired,
  sovStatus: PropTypes.string.isRequired,
  setOpen: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  order: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  users: PropTypes.array.isRequired,
  assignedUserId: PropTypes.number.isRequired,
  userRights: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired
};
export default UpdateOrder;
